import React, { useEffect, useRef, useState } from 'react';
import { processData } from '../data/mock';
import { CheckCircle, Circle } from 'lucide-react';

const ProcessSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const sectionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isVisible) {
      const interval = setInterval(() => {
        setActiveStep((prev) => (prev + 1) % processData.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  return (
    <section ref={sectionRef} id="process" className="py-24 bg-[#04101C] relative overflow-hidden">
      {/* Background Gradient */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#00C4CC]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#00E08F]/10 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            className={`text-3xl md:text-4xl font-bold text-[#F5FDFF] mb-4 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            Our <span className="bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] bg-clip-text text-transparent">Process</span>
          </h2>
          <p
            className={`text-[#F5FDFF]/60 max-w-xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            A proven framework that takes you from idea to launch and beyond.
          </p>
        </div>

        {/* Desktop: Horizontal Timeline */}
        <div className="hidden lg:block">
          {/* Timeline Line */}
          <div className="relative mb-12">
            <div className="h-1 bg-[#F5FDFF]/10 rounded-full">
              <div
                className="h-full bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] rounded-full transition-all duration-500"
                style={{ width: `${((activeStep + 1) / processData.length) * 100}%` }}
              />
            </div>

            {/* Step Markers */}
            <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between">
              {processData.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(index)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    index <= activeStep
                      ? 'bg-gradient-to-r from-[#00C4CC] to-[#00E08F] scale-125'
                      : 'bg-[#F5FDFF]/20 hover:bg-[#F5FDFF]/30'
                  }`}
                >
                  {index <= activeStep ? (
                    <CheckCircle size={16} className="text-[#04101C]" />
                  ) : (
                    <Circle size={16} className="text-[#F5FDFF]/40" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Step Cards - Grid Layout */}
          <div
            ref={scrollRef}
            className="grid grid-cols-6 gap-4"
          >
            {processData.map((step, index) => (
              <div
                key={step.id}
                className={`p-5 rounded-2xl transition-all duration-500 cursor-pointer ${
                  index === activeStep
                    ? 'bg-gradient-to-br from-[#00C4CC]/20 to-[#00E08F]/20 border-2 border-[#00E08F] scale-105 shadow-lg shadow-[#00E08F]/20'
                    : 'bg-[#F5FDFF]/5 border-2 border-transparent hover:border-[#00C4CC]/30'
                } ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
                onClick={() => setActiveStep(index)}
              >
                <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-[#00C4CC] to-[#00E08F] text-[#04101C] text-xs font-bold mb-3">
                  Step {step.id}
                </span>
                <h3 className="text-lg font-bold text-[#F5FDFF] mb-2">{step.title}</h3>
                <p className="text-[#F5FDFF]/60 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile: Vertical Stepper */}
        <div className="lg:hidden space-y-4">
          {processData.map((step, index) => (
            <div
              key={step.id}
              className={`relative pl-10 transition-all duration-500 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              {/* Vertical Line */}
              {index < processData.length - 1 && (
                <div className="absolute left-[15px] top-10 w-0.5 h-full bg-[#F5FDFF]/10" />
              )}

              {/* Step Marker */}
              <div
                className={`absolute left-0 top-0 w-8 h-8 rounded-full flex items-center justify-center ${
                  index <= activeStep
                    ? 'bg-gradient-to-r from-[#00C4CC] to-[#00E08F]'
                    : 'bg-[#F5FDFF]/10'
                }`}
              >
                <span
                  className={`text-xs font-bold ${
                    index <= activeStep ? 'text-[#04101C]' : 'text-[#F5FDFF]/40'
                  }`}
                >
                  {step.id}
                </span>
              </div>

              {/* Content */}
              <div
                className={`p-4 rounded-xl transition-all duration-300 ${
                  index === activeStep
                    ? 'bg-gradient-to-br from-[#00C4CC]/10 to-[#00E08F]/10 border border-[#00E08F]/30'
                    : 'bg-[#F5FDFF]/5'
                }`}
                onClick={() => setActiveStep(index)}
              >
                <h3 className="text-lg font-bold text-[#F5FDFF] mb-1">{step.title}</h3>
                <p className="text-[#F5FDFF]/60 text-sm">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default ProcessSection;
